/**
 * AmberEmbers — Ambient floating ember particle effect
 * 
 * Pure CSS-driven particle system that creates the mystical amber/orange
 * ember look inspired by DECKSTR's glowing aesthetic. Zero dependencies,
 * fully pointer-events transparent, sits behind all UI content.
 */

import { useMemo } from 'react';

interface Ember {
    id: number;
    size: number;       // px
    left: number;       // % horizontal position
    delay: number;      // s animation delay
    duration: number;   // s float-up duration
    drift: number;      // px horizontal sway
    opacity: number;    // 0-1 max opacity
    blur: number;       // px glow blur
    color: string;      // hsl amber variant
    themedColor: string; // guild-theme color (used when <html data-guild> is set)
}

export function AmberEmbers() {
    const embers = useMemo<Ember[]>(() => {
        return Array.from({ length: 28 }, (_, i) => {
            const r = pseudoRandom(i);
            const r2 = pseudoRandom(i + 100);
            const r3 = pseudoRandom(i + 200);
            const r4 = pseudoRandom(i + 300);
            const r5 = pseudoRandom(i + 400);

            // Mix of amber (60%) and purple (40%) — matches extension palette
            const isPurple = pseudoRandom(i + 500) < 0.4;
            const hue = isPurple
                ? 260 + r5 * 30    // Purple hue range: 260-290
                : 20 + r5 * 25;   // Amber hue range: 20-45 (orange to golden)
            const sat = isPurple
                ? 55 + r3 * 20     // Purple: 55-75% saturation
                : 85 + r3 * 15;   // Amber: 85-100% saturation
            const light = isPurple
                ? 55 + r4 * 15     // Purple: 55-70% lightness
                : 50 + r4 * 20;   // Amber: 50-70% lightness

            // Guild-theme equivalent: purple embers -> primary slot, amber embers -> accent slot
            const themedShade = r4 < 0.33 ? 300 : r4 < 0.66 ? 400 : 500;
            const themedColor = `rgb(var(--ds-${isPurple ? 'primary' : 'accent'}-${themedShade}))`;

            return {
                id: i,
                size: 2 + r * 4,                          // 2-6px
                left: r2 * 100,                             // 0-100%
                delay: r3 * 12,                              // 0-12s stagger
                duration: 8 + r4 * 14,                       // 8-22s to float up
                drift: -30 + r5 * 60,                        // -30 to +30px sway
                opacity: 0.15 + r * 0.45,                    // 0.15-0.6
                blur: 1 + r2 * 3,                            // 1-4px glow
                color: `hsl(${hue}, ${sat}%, ${light}%)`,
                themedColor,
            };
        });
    }, []);

    return (
        <div
            className="amber-embers-container"
            aria-hidden="true"
        >
            {/* Ambient edge glow — subtle purple/amber wisps */}
            <div className="amber-edge-glow amber-edge-glow--left" />
            <div className="amber-edge-glow amber-edge-glow--right" />
            <div className="amber-edge-glow amber-edge-glow--bottom" />

            {/* Floating embers */}
            {embers.map((ember) => (
                <div
                    key={ember.id}
                    className="amber-ember"
                    style={{
                        '--ember-size': `${ember.size}px`,
                        '--ember-left': `${ember.left}%`,
                        '--ember-delay': `${ember.delay}s`,
                        '--ember-duration': `${ember.duration}s`,
                        '--ember-drift': `${ember.drift}px`,
                        '--ember-opacity': ember.opacity,
                        '--ember-blur': `${ember.blur}px`,
                        '--ember-color': ember.color,
                        '--ember-themed': ember.themedColor,
                    } as React.CSSProperties}
                />
            ))}
        </div>
    );
}

/**
 * Deterministic pseudo-random based on seed index.
 * Keeps ember positions stable across re-renders.
 */
function pseudoRandom(seed: number): number {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
}

export default AmberEmbers;
